import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Skeleton,
  Box,
  Pagination,
  Button,
} from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import { FaEdit, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../../shared/ui/ConfirmationDialog";
import EmptyState from "../../../shared/ui/EmptyState";

const API_URL = "http://localhost:5000/api/packages/my-packages";

const SkeletonRow = () => (
  <TableRow>
    {[200, 160, 80, 100, 90, 60].map((w, i) => (
      <TableCell key={i}>
        <Skeleton variant="text" width={w} height={20} />
      </TableCell>
    ))}
  </TableRow>
);

const TourPackageTable = () => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [tourPackages, setTourPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchPackages = async (pageNumber = 1) => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_URL}?page=${pageNumber}&limit=${limit}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error("Failed to fetch packages");
        const result = await response.json();
        const data = result.data || result;
        setTourPackages(
          data.map((pkg) => ({
            id: pkg._id,
            name: pkg.basicInfo.tour_name,
            location: `${pkg.basicInfo.city}, ${pkg.basicInfo.state}, ${pkg.basicInfo.country}`,
            duration: pkg.basicInfo.duration || "N/A",
            price: `${pkg.pricing.pricePerPerson?.toLocaleString()} ${pkg.pricing.currency || ""}`,
            createdAt: new Date(pkg.createdAt).toLocaleDateString(),
          }))
        );
        setTotalPages(result.totalPages || 1);
      } catch (error) {
        console.error("Error fetching packages:", error);
        toast.error("Failed to fetch packages");
      } finally {
        setLoading(false);
      }
    };
    fetchPackages(page);
  }, [page]);

  const openDeleteDialog = (id) => { setSelectedId(id); setDialogOpen(true); };
  const closeDeleteDialog = () => { setDialogOpen(false); setSelectedId(null); };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/${selectedId}`, { method: "DELETE" });
      if (!response.ok) throw new Error("Delete failed");
      setTourPackages((prev) => prev.filter((pkg) => pkg.id !== selectedId));
      toast.success("Package deleted successfully");
    } catch (err) {
      toast.error("Error deleting package");
      console.error(err);
    } finally {
      closeDeleteDialog();
    }
  };

  return (
    <Paper elevation={0} sx={{ border: "1px solid #F0F0F5" }}>
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          My Tour Packages
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["Name", "Location", "Duration", "Price", "Created", "Actions"].map((h) => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : tourPackages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 0 }}>
                  <EmptyState
                    icon={<AddBoxOutlinedIcon />}
                    title="No tour packages yet"
                    description="Create your first tour package to start accepting bookings from distributor partners."
                    action={
                      <Button
                        variant="contained"
                        onClick={() => navigate("/create-package")}
                      >
                        Create your first package
                      </Button>
                    }
                    py={8}
                  />
                </TableCell>
              </TableRow>
            ) : (
              tourPackages.map((pkg) => (
                <TableRow
                  key={pkg.id}
                  hover
                  onClick={() => navigate(`/package/${pkg.id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{pkg.name}</TableCell>
                  <TableCell>{pkg.location}</TableCell>
                  <TableCell>{pkg.duration} days</TableCell>
                  <TableCell>{pkg.price}</TableCell>
                  <TableCell>{pkg.createdAt}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      color="info"
                      size="small"
                      onClick={() => navigate(`/edit-package/${pkg.id}`)}
                    >
                      <FaEdit />
                    </IconButton>
                    <IconButton
                      color="warning"
                      size="small"
                      onClick={() => openDeleteDialog(pkg.id)}
                    >
                      <FaTrash />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && totalPages > 1 && (
        <Box display="flex" justifyContent="center" py={2.5}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Box>
      )}

      <ConfirmationDialog
        open={dialogOpen}
        onCancel={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete this package?"
        description="This action is irreversible. All data for this tour package will be permanently removed."
        confirmText="Delete"
        confirmColor="error"
      />
    </Paper>
  );
};

export default TourPackageTable;
